import { useState } from 'react';
import PropTypes from 'prop-types';
import { Row, Col, Card } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import EdiText from 'react-editext';
import { toast } from 'react-toastify';
import Avatar from '../../Avatar';
import { MemberStatus } from '../../common';
import { MemberRole } from '../../team';
import { teamService } from '../../../services';
import { UPDATE_FAILURE_ERROR, TEAM } from '../../../constants';
import Loading from '../../Loader';
import { convertSecondsToHoursAndMinutes } from '../../../helpers';

const TeamMemberCard = ({ cardTitle, teamMemberData, projectId, userRole }) => {
  const [isLoading, setIsLoading] = useState(false);
  const updateRate = (updateData) => {
    setIsLoading(true);
    const params = {
      teamOwnerId: localStorage.getItem('teamOwnerId'),
      projectId,
      ...updateData,
    };
    teamService
      .update_user_rate(params)
      .then((res) => {
        if (res.status) {
          setIsLoading(false);
          toast.success(TEAM.RATE_UPDATE_SUCCESS);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        toast.error(UPDATE_FAILURE_ERROR);
      });
  };
  const tableColumns = [
    {
      name: 'MEMBER',
      selector: (row) => row.name,
      sortable: true,
      cell: (row) => row.member,
    },
    {
      name: 'ONLINE',
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => row.online,
    },
  ];
  if (userRole !== 'member') {
    tableColumns.push(
      {
        name: 'TOTAL HOURS',
        selector: (row) => row.hours,
        sortable: true,
      },
      {
        name: 'TASKS',
        selector: (row) => row.totalTasks,
        sortable: true,
        cell: (row) => row.tasks,
      },
      {
        name: 'HOURLY RATE',
        selector: (row) => row.rate,
        sortable: true,
      }
    );
  }
  const tableData = [];
  const { members } = teamMemberData;
  members.forEach(function (member, index) {
    const { tasks } = member;
    let completeTasks = 0;
    let totalTasks = 0;
    if (tasks && tasks.length >= 1) {
      completeTasks =
        tasks.filter((task) => task.status === 'complete').length >= 1
          ? tasks.filter((task) => task.status === 'complete')[0].tasks
          : 0;
      totalTasks = tasks.reduce((total, task) => total + task.tasks, 0);
    }
    const details = {
      id: `tm${index}`,
      member: (
        <Row>
          <Col>
            <div className="d-flex align-items-center">
              <div className="avatar me-2">
                <Avatar
                  firstName={member.firstName}
                  lastName={member.lastName}
                  imgPath={member.avatar ? member.avatar : ''}
                />
              </div>
              <div className="mt-3">
                <span>
                  {member.firstName} {member.lastName}{' '}
                  {member.status === 'inactive' && ' (Inactive)'}
                </span>
                <p className="role mt-2">
                  <MemberRole role={'role' in member ? member.role : ''} />
                </p>
              </div>
            </div>
          </Col>
        </Row>
      ),
      online: <MemberStatus memberId={member.userId} />,
      status: member.status,
      name: `${member.firstName} ${member.lastName}`,
    };

    if (userRole !== 'member') {
      details.hours = member.workSession.totalWorksessionSeconds
        ? convertSecondsToHoursAndMinutes(
            member.workSession.totalWorksessionSeconds
          )
        : 0;
      details.tasks = (
        <>
          {tasks && tasks.length >= 1 ? (
            <Row className="align-items-center no-gutters progress-info w-100">
              <Col className="col-auto p-0">
                {completeTasks}/{totalTasks}
              </Col>
              <Col md={6} xs={12}>
                <div className="progress progress-sm">
                  {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
                  <div
                    className="progress-bar"
                    role="progressbar"
                    style={{
                      width: `${(completeTasks / totalTasks) * 100}%`,
                    }}
                    aria-valuenow={{
                      width: `${(completeTasks / totalTasks) * 100}%`,
                    }}
                    aria-valuemin="0"
                    aria-valuemax="100"
                  />
                </div>
              </Col>
            </Row>
          ) : (
            <div className="col-auto">
              <span className="badge bg-secondary-soft">Not available</span>
            </div>
          )}
        </>
      );
      details.rate = (
        <EdiText
          type="text"
          validationMessage="Invalid rate"
          validation={(val) =>
            val.match(/^(?!0\d)\d*(\.\d+)?$/) &&
            val >= 0 &&
            val.trim().length > 0
          }
          value={
            member.rates.costRate ? member.rates.costRate.toFixed(2) : '0.00'
          }
          showButtonsOnHover
          className="link-primary"
          onSave={(value) =>
            updateRate({
              userId: member.userId,
              costRate: value,
            })
          }
          editOnViewClick
          cancelOnUnfocus
          hideIcons
          editButtonContent={<i className="mdi mdi-pencil-outline" />}
          saveButtonContent={<i className="mdi mdi-check" />}
          saveButtonClassName="btn btn-primary btn-sm me-2"
          cancelButtonContent={<i className="mdi mdi-close" />}
          cancelButtonClassName="btn btn-white btn-sm"
          inputProps={{ maxLength: 8 }}
        />
      );
      details.totalTasks = totalTasks;
    }
    tableData.push(details);
  });

  return (
    <Card>
      <Card.Header>
        <h4 className="m-0">{cardTitle}</h4>
      </Card.Header>
      <Card.Body className="p-0">
        {isLoading && <Loading contentAreaOnly />}
        <DataTable columns={tableColumns} data={tableData} />
      </Card.Body>
    </Card>
  );
};

TeamMemberCard.propTypes = {
  cardTitle: PropTypes.string,
  teamMemberData: PropTypes.object,
  projectId: PropTypes.string,
  userRole: PropTypes.string,
};
export default TeamMemberCard;
